import { ContractRevenueItem, ScheduledPayment } from "@/pages/contracts/types";

// A revenue item in an unsaved session; deletion is a flag so the row stays restorable.
export interface DraftRevenueItem extends ContractRevenueItem {
  draftDeleted?: boolean;
}

export type DraftRowStatus = "modified" | "deleted" | "new" | null;

// Marks a row added inside the session, which deletes outright rather than flagging.
export const DRAFT_CONTRACT_ID = "draft";

// Fields whose change marks a row as "Modified" against the baseline.
export const MODIFIED_COMPARE_FIELDS = [
  "serviceStartDate",
  "serviceEndDate",
  "billingStartDate",
  "billingEndDate",
  "customOptOutDate",
  "quantity",
  "pricePerUnit",
  "isDiscountPercentage",
  "discountAmount",
  "totalLocal",
  "autoRenewal",
  "customBillingFrequency",
  "customServiceDuration",
  "serviceItemPricingId",
  "eqPaymentsNumOfPayments",
  "eqPaymentsBillingFrequency"
] as const satisfies ReadonlyArray<keyof ContractRevenueItem>;

// The drawer round-trips a payment to a calendar day and reads a null amount as 0,
// so a raw compare would mark an untouched schedule as changed.
const paymentSignature = (payment: ScheduledPayment): string =>
  [
    payment.paymentNumber,
    String(payment.billingDate).split("T")[0],
    payment.amount ?? 0,
    payment.percentage ?? 0
  ].join("|");

// Reallocating between payments leaves totalLocal untouched, so the schedule
// is the only place that change is visible.
const hasPaymentChanges = (
  item: DraftRevenueItem,
  source: ContractRevenueItem
): boolean => {
  // The drawer only ever loads and resubmits the live rows.
  const live = (payments: ScheduledPayment[] | undefined) =>
    (payments ?? []).filter((payment) => !payment.isDeleted);
  const pending = live(item.scheduledPayments);
  const original = live(source.scheduledPayments);
  if (pending.length !== original.length) return true;
  const originalSignatures = original.map(paymentSignature).sort();
  const pendingSignatures = pending.map(paymentSignature).sort();
  return pendingSignatures.some(
    (signature, index) => signature !== originalSignatures[index]
  );
};

export const getDraftRowStatus = (
  item: DraftRevenueItem,
  baseline: Record<string, ContractRevenueItem>
): DraftRowStatus => {
  if (item.draftDeleted) return "deleted";
  const source = baseline[item.id];
  if (!source) return "new";
  const isModified =
    MODIFIED_COMPARE_FIELDS.some((field) => item[field] !== source[field]) ||
    hasPaymentChanges(item, source);
  return isModified ? "modified" : null;
};

// The note is left out of MODIFIED_COMPARE_FIELDS: it is annotation, so a
// changed note neither marks the row Modified nor counts as a terms change.
// It still has to be savable on its own.
export const hasDraftNoteChange = (
  item: DraftRevenueItem,
  baseline: Record<string, ContractRevenueItem>
): boolean => {
  const source = baseline[item.id];
  if (!source || item.draftDeleted) return false;
  return (item.note ?? null) !== (source.note ?? null);
};
